import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import 'rxjs/add/operator/map';
import { SidenavService } from '../core/sidenav/sidenav.service';
import { SidenavItem } from '../core/sidenav/sidenav-item/sidenav-item.interface';

import { ConfigData } from '../app.config';
import { LanguageService } from './language.service';
import { Router } from '@angular/router';
import { PERMISSION_CODES } from 'app/constants/permission-codes';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  loginApiUrl: string = ConfigData.loginUrl + '/login';
  logoutApiUrl: string = ConfigData.loginUrl + '/auth/logout';
  recoveryApiUrl: string = ConfigData.loginUrl + '/recovery-password';
  sideNav: SidenavService;

  private permissions: string[] = [];
  private excludedAreas: string[] = [];

  private userRole: string;

  constructor(
    private http: HttpClient,
    private sidenavService: SidenavService,
    private languageService: LanguageService,
    private router: Router
  ) { }

  login(username: string, password: string) {
    return this.http
      .post<any>(this.loginApiUrl, { username: username, password: password })
      .map((res: any) => {
        if (res && res.token) {
          localStorage.setItem('currentUser', JSON.stringify({ res }));
          localStorage.setItem(
            'currentToken',
            JSON.stringify({ token: res.token })
          );
          localStorage.setItem(
            'loginDate',
            JSON.stringify({ timestamp: new Date().getTime() })
          );
          this.configureMenu();
        }
      });
  }

  private setWindowTitle(): void {
    document.title = `${ConfigData.clientName}`;
  }

  configureMenu() {
    this.setWindowTitle();
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    this.permissions = currentUser
      ? currentUser.res.permissions
      : [];

    if (!this.permissions.length) {
      return;
    }

    this.userRole = currentUser ? currentUser.res.role : '';

    if (ConfigData.excludeAreas && ConfigData.excludeAreas.length) {
      ConfigData.excludeAreas.forEach(area => {
        const index = this.permissions.indexOf(area);
        if (index >= 0) {
          this.permissions.splice(index, 1);
        }
      });

      this.excludedAreas = ConfigData.excludeAreas;
    }

    const menu: SidenavItem[] = [];
    const pass: boolean = this.permissions.indexOf('all') > -1 || this.userRole === 'superadmin';

    this.sidenavService.items = [];

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.STATO_MACCHINE) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.STATO_MACCHINE) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.statoMacchine'),
        routeOrFunction: '/',
        icon: 'av_timer',
        position: 10,
        pathMatchExact: true
      });
    }

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.FRONTEND) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.FRONTEND) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.frontEnd'),
        routeOrFunction: 'front-ends',
        icon: 'queue_play_next',
        position: 15,
        pathMatchExact: true
      });
    }

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.CENTRO_PREVIEW) > -1
    ) && !(this.excludedAreas.indexOf(PERMISSION_CODES.CENTRO_PREVIEW) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.centro-preview'),
        routeOrFunction: '/preview-center',
        icon: 'desktop_windows',
        position: 16,
        pathMatchExact: true
      });
    }

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.CLIENT) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.CLIENT) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.client'),
        routeOrFunction: '/clients',
        icon: 'devices',
        position: 20,
        pathMatchExact: false
      });
    }

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.INDICI_ALIAS) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.INDICI_ALIAS) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.indiciAlias'),
        routeOrFunction: '/indici-alias',
        icon: 'clear_all',
        position: 21,
        pathMatchExact: false
      });
    }

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.GESTIONE_CRAWLING) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.GESTIONE_CRAWLING) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.gestioneCrawling'),
        routeOrFunction: '/gestione-crawling/web',
        icon: 'compare_arrows',
        position: 22,
        pathMatchExact: false
      });
    }

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.GESTIONE_UTENZE) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.GESTIONE_UTENZE) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.gestioneUtenze'),
        routeOrFunction: '/user-role',
        icon: 'people',
        position: 23,
        pathMatchExact: false
      });
    }
    if ((pass || this.permissions.indexOf(PERMISSION_CODES.CONFIGURAZIONE) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.CONFIGURAZIONE) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.configurazione'),
        routeOrFunction: '/configuration',
        icon: 'settings',
        position: 25,
        pathMatchExact: false
      });
    }

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.ONEBOX) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.ONEBOX) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.onebox'),
        routeOrFunction: '/onebox',
        icon: 'dns',
        position: 26,
        pathMatchExact: true
      });
    }

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.DIFFERENZIAZIONE_RISULTATI) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.DIFFERENZIAZIONE_RISULTATI) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.differenziazioneRisultati'),
        routeOrFunction: '/policy-biasing',
        icon: 'transform',
        position: 27,
        pathMatchExact: true
      });
    }

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.IMPOSTAZIONI_QUERY) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.IMPOSTAZIONI_QUERY) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.impostazioniQuery'),
        routeOrFunction: '/dictionaries',
        icon: 'g_translate',
        position: 40,
        pathMatchExact: false
      });
    }

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.SUGGESTION) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.SUGGESTION) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.suggestion'),
        routeOrFunction: '/suggestion',
        icon: 'wb_incandescent',
        position: 42,
        pathMatchExact: true
      });
    }

    if (
      (pass || this.permissions.indexOf(PERMISSION_CODES.GSA_CONFIGURATION_IMPORTER) > -1) &&
      !(this.excludedAreas.indexOf(PERMISSION_CODES.GSA_CONFIGURATION_IMPORTER) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.gsaConfigurationImporter'),
        routeOrFunction: '/gsa-configuration-importer',
        icon: 'get_app',
        position: 45,
        pathMatchExact: false
      });
    }

    if ((pass || this.permissions.indexOf(PERMISSION_CODES.REPORT) > -1)
      && !(this.excludedAreas.indexOf(PERMISSION_CODES.REPORT) > -1)) {
      menu.push({
        name: this.languageService.getLabel('menu.report'),
        routeOrFunction: '/reports/globalreports',
        icon: 'insert_chart',
        position: 50,
        pathMatchExact: true
      });
    }
    // Send all created Items to SidenavService
    menu.forEach(item => this.sidenavService.addItem(item));
  }

  logout(): void {
    this.http.post(this.logoutApiUrl, {}).subscribe(resp => {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('currentToken');
    });

    this.router.navigate(['/login']);

  }

  retrivePass(username: string) {
    return this.http.post<any>(
      this.recoveryApiUrl,
      { username: username },
      { headers: { 'Content-Type': 'application/json' } }
    );
  }

  userHasPermission(permissionCode: string): boolean {
    // L'utente ha il permesso se:
    // - Ha il permesso all che consente di accedere a tutte le aree dell'applicazione
    // - Ha il permesso passato alla funzione nella lista dei suoi permessi (presa dal token) e
    //   allo stesso tempo questo non è presente nella lista dei permessi da escludere
    //   (che viene settata nel env.json)
    return (this.permissions.indexOf('all') > -1 ||
      this.permissions.indexOf(permissionCode) > -1)
      && !(this.excludedAreas.indexOf(permissionCode) > -1);
  }

  userIsAdmin(): boolean {
    return this.userRole === 'admin' || this.userRole === 'superadmin';
  }

  /**
   * Controlla se lìuser è configurato correttamente ovvero se ha almeno una permission associata
   */
  userIsEnabled(): boolean {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const permissions = currentUser
      ? currentUser.res.permissions
      : [];
    return permissions.length > 0;
  }
}
